// =============================================================================
// Roll Result Formatters
// Shows the full calculation: d20 roll, modifier, each damage row with dice
// results, and the correct per-row crit multiplication.
// =============================================================================
import type {
  CompletedRollResult, DamageRollResult, PendingRollResult, SpecialDamageType, WeaponSpecialEffectResult,
} from '../../../backend/shared/types/index';
import { SPECIAL_DAMAGE_LABELS } from '../../../backend/shared/types/index';

// ---------------------------------------------------------------------------
// Damage-row formatter
// ---------------------------------------------------------------------------
type CritStyle = 'standard' | 'perfect' | undefined;

/** Format component list as a string: '+8 (BAB Max) +5 (STR Modifier) +1 (Weapon Focus)' */
function fmtComponents(comps: import('../../../backend/shared/types/index').ResolvedModComponent[]): string {
  return comps.map((c) => `${c.value >= 0 ? '+' : ''}${c.value} (${c.label})`).join(' ');
}

function maxDiceTotal(d: DamageRollResult): number {
  const match = /^(\d+)d(\d+)$/i.exec(d.diceExpr.trim());
  return match
    ? Number(match[1]) * Number(match[2])
    : d.rolls.reduce((s, r) => s + r, 0);
}

function critDamage(d: DamageRollResult, is5e: boolean, critStyle?: CritStyle): number {
  if (d.critMult <= 1) return d.total;
  if (is5e && critStyle === 'perfect') {
    return maxDiceTotal(d) + d.rolls.reduce((s, r) => s + r, 0) + d.modifier;
  }
  if (is5e) return d.rolls.reduce((s, r) => s + r, 0) * 2 + d.modifier;
  return d.total * d.critMult;
}

/** Format one resolved damage row. Returns Discord markdown and plain-text versions. */
function fmtDamageRow(
  d:    DamageRollResult,
  isHitCrit: boolean,
  is5e: boolean,
  critStyle?: CritStyle,
): { discord: string; plain: string } {
  const hasDice  = d.rolls.length > 0;
  const rollsStr = hasDice ? `[${d.rolls.join(', ')}]` : '';
  const diceSum  = d.rolls.reduce((s, r) => s + r, 0);
  const modPart  = d.modifier !== 0
    ? ` ${d.modifier > 0 ? '+' : ''}${d.modifier}`
    : '';
  const notesStr = d.notes ? `  ${d.notes}` : '';
  const notMult  = isHitCrit && d.critMult <= 1;
  const doMult   = isHitCrit && d.critMult > 1;

  // Dice expression prefix ("2d6 [4, 3]" or just "[4, 3]" for flat)
  const exprPrefix = hasDice
    ? `${d.diceExpr.replace(/[+-].*/, '')} \`${rollsStr}\`` // strip modifier from expr display
    : '';

  if (!doMult) {
    // Normal hit or critMult=1 — show as-is; mark not-multiplied on a crit
    const notMultNote = notMult ? ' *(crit ×1 — not multiplied)*' : '';
    const notMultPlain = notMult ? ' (crit x1, not multiplied)' : '';
    if (hasDice) {
      return {
        discord: `${exprPrefix}${d.resolvedModComponents?.length ? ' ' + fmtComponents(d.resolvedModComponents) : modPart} = **${d.total}**${notesStr}${notMultNote}`,
        plain:   `${d.diceExpr.replace(/[+-].*/, '')} ${rollsStr}${d.resolvedModComponents?.length ? ' ' + fmtComponents(d.resolvedModComponents) : modPart} = ${d.total}${notesStr}${notMultPlain}`,
      };
    }
    return {
      discord: `**${d.total}**${notesStr}${notMultNote}`,
      plain:   `${d.total}${notesStr}${notMultPlain}`,
    };
  }

  // Critical hit — multiply
  if (is5e) {
    if (critStyle === 'perfect') {
      const maxDice = maxDiceTotal(d);
      const critTotal = maxDice + diceSum + d.modifier;
      return {
        discord: `${exprPrefix}${modPart} = max(${maxDice}) + crit ${diceSum} = **${critTotal}**${notesStr}`,
        plain:   `${d.diceExpr.replace(/[+-].*/, '')} ${rollsStr}${modPart} = max(${maxDice}) + crit ${diceSum} = ${critTotal}${notesStr}`,
      };
    }
    // 5e: dice doubled, modifier once
    const critTotal = diceSum * 2 + d.modifier;
    return {
      discord: `${exprPrefix}${modPart} = ${d.total} *(dice ×2 → +${diceSum})* = **${critTotal}**${notesStr}`,
      plain:   `${d.diceExpr.replace(/[+-].*/, '')} ${rollsStr}${modPart} = ${d.total} (dice x2, +${diceSum}) = ${critTotal}${notesStr}`,
    };
  } else {
    // 3.5 / PF1: full expression × critMult
    const critTotal = d.total * d.critMult;
    return {
      discord: `${exprPrefix}${modPart} = ${d.total} ×${d.critMult} = **${critTotal}**${notesStr}`,
      plain:   `${d.diceExpr.replace(/[+-].*/, '')} ${rollsStr}${modPart} = ${d.total} x${d.critMult} = ${critTotal}${notesStr}`,
    };
  }
}

/** Compute the actual damage contributed by one row on a crit. */
function rowCritDamage(d: DamageRollResult, is5e: boolean, critStyle?: CritStyle): number {
  return critDamage(d, is5e, critStyle);
}

function fmtSpecialEffect(e: WeaponSpecialEffectResult): { discord: string; plain: string } {
  const name = e.property === 'vorpal' ? 'Vorpal' : 'Sharpness';
  if (e.property === 'sharpness') {
    const limb = e.limb ? `: ${e.limb}` : '';
    const eligibility = e.eligibility ? ` (${e.eligibility})` : '';
    return {
      discord: `**${name}** triggers${limb} severed${eligibility}`,
      plain:   `${name} triggers${limb} severed${eligibility}`,
    };
  }
  return {
    discord: `**${name}** triggers: ${e.effect}`,
    plain:   `${name} triggers: ${e.effect}`,
  };
}

// ---------------------------------------------------------------------------
// Public formatters
// ---------------------------------------------------------------------------

export function formatForDiscord(result: CompletedRollResult): string {
  const lines: string[] = [];
  const is5e = result.gameSystem === 'dnd5e';

  lines.push(result.characterName
    ? `⚔️  **${result.routineName}**  —  ${result.characterName}`
    : `⚔️  **${result.routineName}**`);
  lines.push('');

  for (const atk of result.resolvedAttacks) {
    const isCrit    = atk.isCrit && atk.isHit;
    const modStr    = `${atk.attackMod >= 0 ? '+' : ''}${atk.attackMod}`;
    const compStr   = atk.resolvedAttackComponents?.length
      ? ' ' + atk.resolvedAttackComponents
          .map((c) => `${c.value >= 0 ? '+' : ''}${c.value} (${c.label})`).join(' ')
      : ` ${modStr}`;
    const rollLine  = atk.advantageDie !== undefined
    ? `\`d20 [${Math.min(atk.d20, atk.advantageDie)}, **${atk.d20}**]\` ${atk.rollMode === 'advantage' ? '(adv)' : '(dis)'}${compStr} = **${atk.total}** vs ${atk.vsTarget}`
    : `\`d20 [${atk.d20}]\`${compStr} = **${atk.total}** vs ${atk.vsTarget}`;
    const critLabel = isCrit
      ? (is5e ? (atk.critStyle === 'perfect' ? ' *(Perfect Crit)*' : ' *(Crit — dice ×2)*') : ` *(Crit)*`)
      : '';

    if (!atk.isHit) {
      lines.push(`❌  **${atk.label}**   ${rollLine}`);
    } else {
      lines.push(`${isCrit ? '⚡' : '✅'}  **${atk.label}**${critLabel}   ${rollLine}`);

      if (atk.damage.length === 0) {
        lines.push('   └ *(no damage roll)*');
      } else {
        let atkTotal = 0;
        for (const d of atk.damage) {
          const line = fmtDamageRow(d, isCrit, is5e, atk.critStyle);
          lines.push(`   └ ${line.discord}`);
          atkTotal += isCrit ? rowCritDamage(d, is5e, atk.critStyle) : d.total;
        }
        if (isCrit) {
          for (const d of atk.onCritExtraDamage) {
            const line = fmtDamageRow(d, false, false);
            const notesStr = d.notes ? `  ${d.notes}` : '';
            lines.push(`   └ ${line.discord.replace(notesStr, '')}${notesStr} *(crit bonus)*`);
            atkTotal += d.total;
          }
        }
        if (atk.damage.length > 1 || (isCrit && atk.onCritExtraDamage.length > 0) || isCrit) {
          lines.push(`   └ Attack total: **${atkTotal} damage**`);
        }
      }
      for (const effect of atk.specialEffects ?? []) {
        lines.push(`   └ ${fmtSpecialEffect(effect).discord}`);
      }
    }

    lines.push('');
  }

  // Conditional bonus damage results
  for (const c of result.conditionalDamageResults ?? []) {
    const icon = c.triggered ? '⚡' : '○';
    const lbl  = c.label ? `**${c.label}**` : '**Conditional Bonus**';
    if (c.triggered) {
      lines.push(`${icon}  ${lbl}   **+${c.total} damage**`);
      for (const d of c.damage) {
        const line = fmtDamageRow(d, false, false);
        lines.push(`   └ ${line.discord}`);
      }
    } else {
      lines.push(`${icon}  ${lbl}   *(not triggered)*`);
    }
  }

  // Bonus attack results (Phase 5: conditional full attacks)
  for (const ba of result.bonusAttackResults ?? []) {
    const icon = ba.triggered ? '⚡' : '○';
    const lbl  = ba.label ? `**${ba.label}**` : '**Bonus Attack**';
    const countTag = ba.triggered && ba.attackResults.length > 1 ? ` ×${ba.attackResults.length}` : '';
    if (ba.triggered) {
      lines.push(`${icon}  ${lbl}${countTag}   *(triggered)*`);
      for (const atk of ba.attackResults) {
        const isCrit = atk.isCrit;
        const modStr = `${atk.attackMod >= 0 ? '+' : ''}${atk.attackMod}`;
        lines.push(`   ├─ ${atk.label}  \`d20[${atk.d20}] ${modStr} = ${atk.total}\`${isCrit ? (is5e && atk.critStyle === 'perfect' ? ' *(Perfect Crit)*' : ' *(Crit)*') : ''}`);
        let dmgTotal = 0;
        for (const d of atk.damage) {
          const line = fmtDamageRow(d, isCrit, is5e, atk.critStyle);
          const rowTotal = isCrit ? rowCritDamage(d, is5e, atk.critStyle) : d.total;
          dmgTotal += rowTotal;
          lines.push(`   └ ${line.discord}`);
        }
        for (const effect of (atk as any).specialEffects ?? []) {
          lines.push(`   └ ${fmtSpecialEffect(effect).discord}`);
        }
        lines.push(`   └ Damage total: **${dmgTotal}**`);
      }
    } else {
      lines.push(`${icon}  ${lbl}   *(condition not met)*`);
    }
  }

  lines.push('─────────────────────────────────');
  const specialEntriesD = Object.entries(result.specialDamageTotals ?? {}) as [SpecialDamageType, number][];
  const anyDmgD = result.resolvedAttacks.some((a) => a.damage.length > 0)
    || (result.conditionalDamageResults ?? []).some((c) => c.triggered)
    || (result.bonusAttackResults ?? []).some((b) => b.triggered && b.attackResults.length > 0)
    || result.resolvedAttacks.some((a) => (a.specialEffects?.length ?? 0) > 0)
    || specialEntriesD.length > 0;
  if (anyDmgD) {
    if (result.totalDamage > 0 || specialEntriesD.length === 0) {
      lines.push(`**Total: ${result.totalDamage} ${specialEntriesD.length > 0 ? 'HP ' : ''}damage**  ·  ${result.hits} / ${result.totalAttacks} hits`);
    } else {
      lines.push(`${result.hits} / ${result.totalAttacks} hits`);
    }
    for (const [type, amt] of specialEntriesD) {
      lines.push(`**${amt} ${SPECIAL_DAMAGE_LABELS[type]}**`);
    }
  } else {
    lines.push(`**${result.hits}/${result.totalAttacks} check${result.totalAttacks !== 1 ? 's' : ''} succeeded**`);
  }
  return lines.join('\n');
}

export function formatForClipboard(result: CompletedRollResult): string {
  const lines: string[] = [];
  const is5e = result.gameSystem === 'dnd5e';

  lines.push(result.characterName
    ? `${result.routineName} — ${result.characterName}`
    : result.routineName);
  lines.push('');

  for (const atk of result.resolvedAttacks) {
    const isCrit   = atk.isCrit && atk.isHit;
    const modStr   = `${atk.attackMod >= 0 ? '+' : ''}${atk.attackMod}`;
    const compStrP  = atk.resolvedAttackComponents?.length
      ? ' ' + atk.resolvedAttackComponents
          .map((c) => `${c.value >= 0 ? '+' : ''}${c.value} (${c.label})`).join(' ')
      : ` ${modStr}`;
    const rollLine = atk.advantageDie !== undefined
    ? `d20 [${Math.min(atk.d20, atk.advantageDie)}, ${atk.d20}] ${atk.rollMode === 'advantage' ? '(adv)' : '(dis)'}${compStrP} = ${atk.total} vs ${atk.vsTarget}`
    : `d20 [${atk.d20}]${compStrP} = ${atk.total} vs ${atk.vsTarget}`;
    const status   = !atk.isHit ? 'MISS' : isCrit ? (is5e ? (atk.critStyle === 'perfect' ? 'PERFECT CRIT' : 'CRIT (dice x2)') : 'CRIT') : 'HIT ';

    lines.push(`${status}  ${atk.label}   ${rollLine}`);

    if (atk.isHit) {
      if (atk.damage.length === 0) {
        lines.push('      (no damage roll)');
      } else {
        let atkTotal = 0;
        for (const d of atk.damage) {
          const line = fmtDamageRow(d, isCrit, is5e, atk.critStyle);
          lines.push(`      ${line.plain}`);
          atkTotal += isCrit ? rowCritDamage(d, is5e, atk.critStyle) : d.total;
        }
        if (isCrit) {
          for (const d of atk.onCritExtraDamage) {
            const line = fmtDamageRow(d, false, false);
            const notesStr = d.notes ? `  ${d.notes}` : '';
            lines.push(`      ${line.plain.replace(notesStr, '')}${notesStr} (crit bonus)`);
            atkTotal += d.total;
          }
        }
        if (atk.damage.length > 1 || (isCrit && atk.onCritExtraDamage.length > 0) || isCrit) {
          lines.push(`      Attack: ${atkTotal} damage`);
        }
      }
      for (const effect of atk.specialEffects ?? []) {
        lines.push(`      ${fmtSpecialEffect(effect).plain}`);
      }
    }

    lines.push('');
  }

  for (const c of result.conditionalDamageResults ?? []) {
    const lbl = c.label || 'Conditional Bonus';
    if (c.triggered) {
      lines.push(`⚡  ${lbl}   +${c.total} damage`);
      for (const d of c.damage) {
        const line = fmtDamageRow(d, false, false);
        lines.push(`      ${line.plain}`);
      }
    } else {
      lines.push(`○  ${lbl}   (not triggered)`);
    }
  }

  // Bonus attack results (Phase 5: conditional full attacks)
  for (const ba of result.bonusAttackResults ?? []) {
    const lbl = ba.label || 'Bonus Attack';
    const countTagP = ba.triggered && ba.attackResults.length > 1 ? ` x${ba.attackResults.length}` : '';
    if (ba.triggered) {
      lines.push(`⚡  ${lbl}${countTagP}   (triggered)`);
      for (const atk of ba.attackResults) {
        const isCrit = atk.isCrit;
        const modStr = `${atk.attackMod >= 0 ? '+' : ''}${atk.attackMod}`;
        lines.push(`      ${atk.label}: d20 [${atk.d20}] ${modStr} = ${atk.total}${isCrit ? ' CRIT' : ''}`);
        let dmgTotal = 0;
        for (const d of atk.damage) {
          const line = fmtDamageRow(d, isCrit, is5e, atk.critStyle);
          lines.push(`         ${line.plain}`);
          dmgTotal += isCrit ? rowCritDamage(d, is5e, atk.critStyle) : d.total;
        }
        for (const effect of (atk as any).specialEffects ?? []) {
          lines.push(`         ${fmtSpecialEffect(effect).plain}`);
        }
        lines.push(`         Damage: ${dmgTotal}`);
      }
    } else {
      lines.push(`○  ${lbl}   (condition not met)`);
    }
  }

  lines.push('─────────────────────────────────');
  const specialEntriesP = Object.entries(result.specialDamageTotals ?? {}) as [SpecialDamageType, number][];
  const anyDmgP = result.resolvedAttacks.some((a) => a.damage.length > 0)
    || (result.conditionalDamageResults ?? []).some((c) => c.triggered)
    || (result.bonusAttackResults ?? []).some((b) => b.triggered && b.attackResults.length > 0)
    || result.resolvedAttacks.some((a) => (a.specialEffects?.length ?? 0) > 0)
    || specialEntriesP.length > 0;
  if (anyDmgP) {
    if (result.totalDamage > 0 || specialEntriesP.length === 0) {
      lines.push(`Total: ${result.totalDamage} ${specialEntriesP.length > 0 ? 'HP ' : ''}damage  ·  ${result.hits}/${result.totalAttacks} hits`);
    } else {
      lines.push(`${result.hits}/${result.totalAttacks} hits`);
    }
    for (const [type, amt] of specialEntriesP) {
      lines.push(`${amt} ${SPECIAL_DAMAGE_LABELS[type]}`);
    }
  } else {
    lines.push(`${result.hits}/${result.totalAttacks} check${result.totalAttacks !== 1 ? 's' : ''} succeeded`);
  }
  return lines.join('\n');
}

// =============================================================================
// Roll20 Mod-script dispatch formatters
//
// These produce compact `!darkfeld-roll <json>` command strings that are
// injected into Roll20's chat textarea by the browser plugin.  Because they
// begin with `!`, Roll20 routes them to the DarkfeldExport Mod script rather
// than echoing them to the public chat pane.  The Mod script parses the
// payload and calls sendChat() with styled HTML, producing two separate chat
// cards per routine execution:
//
//   formatForRoll20Attacks  — Phase A card: attack roll totals, sent immediately
//   formatForRoll20         — Phase C card: confirmed hits + damage, sent after
//                             the player selects which attacks landed
//
// ─────────────────────────────────────────────────────────────────────────────
// PAYLOAD CONTRACT — keep in sync with tools/roll20/DarkfeldExport.js
// ─────────────────────────────────────────────────────────────────────────────
// The JSON shape produced here is the ONLY interface between this file and the
// Mod script.  Whenever a field is added, removed, or renamed:
//
//   1. Update the corresponding handler in DarkfeldExport.js
//      (buildPhaseCHtml for Phase C, buildPhaseAHtml for Phase A).
//   2. Bump ALL THREE version strings in DarkfeldExport.js:
//        - header comment  "Roll20 Mod Script vX.Y.Z"
//        - CHAT_VERSION    var CHAT_VERSION = 'X.Y.Z';
//        - VERSION         var VERSION      = 'X.Y.Z';
//      patch for new fields on existing keys; minor for new top-level keys.
//   3. Tell the user: the Mod script is manually installed — they must copy
//      the updated file into the Roll20 Mod editor and save it.
// =============================================================================

// ---------------------------------------------------------------------------
// Shared compact types (serialised into the JSON payload)
// ---------------------------------------------------------------------------

/** One attack entry in a Phase A (attack-rolls) dispatch payload. */
interface R20AttackEntry {
  l:     string;   // label
  d20:   number;   // kept d20 value
  mod:   number;   // effective attack modifier
  tot:   number;   // d20 + mod
  crit:  boolean;  // true when the natural d20 is in the threat range
  vs:    string;   // vsTarget (e.g. "AC", "Touch AC")
  /** Discarded d20 when advantage/disadvantage was active. */
  adv?:  number;
  /** 'advantage' | 'disadvantage' — present when adv is set. */
  mode?: 'advantage' | 'disadvantage';
}

/** One damage row entry in a Phase C (damage-results) dispatch payload. */
interface R20DmgRow {
  tot:  number;   // final damage total for this row (post-crit multiplication)
  note: string;   // damage type / description (e.g. "Slashing", "Fire")
  /**
   * Human-readable label for the damage cell (e.g. "Damage ×3", "Bonus (Crit)",
   * "Con Damage").  When absent the Mod script renders the default "Damage" label.
   */
  lbl?:  string;
  /**
   * True when this is a special (non-HP) damage row — ability score damage or
   * negative levels.  The Mod script renders these with a distinct amber colour
   * so players can tell them apart from hit-point damage at a glance.
   */
  sp?:   boolean;
  /** Dice expression without modifiers, e.g. "2d6".  Absent for flat-modifier-only rows. */
  expr?: string;
  /** Individual die results.  Absent for flat-modifier-only rows. */
  dice?: number[];
  /** Flat modifier applied to the roll.  Omitted when zero. */
  dmod?: number;
}

interface R20EffectRow {
  l: string;
  t: string;
}

/** One attack entry in a Phase C (damage-results) dispatch payload. */
interface R20ResultEntry {
  l:       string;      // label
  d20:     number;
  mod:     number;
  tot:     number;      // attack total
  hit:     boolean;
  crit:    boolean;     // confirmed crit (hit && isCrit)
  vs?:     string;      // vsTarget (e.g. "AC", "Touch AC")
  /** Discarded d20 when advantage/disadvantage was active. */
  adv?:    number;
  /** 'advantage' | 'disadvantage' — present when adv is set. */
  mode?:   'advantage' | 'disadvantage';
  dmg?:    R20DmgRow[]; // absent or empty when miss
  fx?:     R20EffectRow[]; // confirmed weapon-property effects
  atkTot?: number;      // total attack damage across all dmg rows
}

/** Triggered conditional-damage summary entry. */
interface R20CondEntry {
  l:   string;  // label
  tot: number;  // total triggered damage
}

/** Triggered bonus-attack summary entry. */
interface R20BonusEntry {
  l:   string;  // label
  cnt: number;  // number of attacks that hit
  tot: number;  // total damage from all bonus attacks
}

// ---------------------------------------------------------------------------
// Phase A — announce attack rolls
// ---------------------------------------------------------------------------

/**
 * Produce a `!darkfeld-roll` command string for the Phase A (attack-rolls)
 * dispatch.  The plugin injects this into Roll20's chat textarea immediately
 * after rollPhaseA so the table sees attack totals before hit selection.
 */
export function formatForRoll20Attacks(pending: PendingRollResult): string {
  const atks: R20AttackEntry[] = pending.attackResults.map((atk) => {
    const entry: R20AttackEntry = {
      l:    atk.label,
      d20:  atk.d20,
      mod:  atk.attackMod,
      tot:  atk.total,
      crit: atk.isThreat,
      vs:   atk.vsTarget,
    };
    if (atk.advantageDie !== undefined) {
      entry.adv  = atk.advantageDie;
      entry.mode = atk.rollMode as 'advantage' | 'disadvantage';
    }
    return entry;
  });

  const payload: Record<string, unknown> = {
    v:     1,
    ph:    'A',
    chr:   pending.characterName ?? '',
    rtn:   pending.routineName,
    atks,
    mods:  pending.appliedModifierNames ?? [],
    theme: pending.r20Theme ?? 'default',
  };

  return `!darkfeld-roll ${JSON.stringify(payload)}`;
}

// ---------------------------------------------------------------------------
// Phase C — announce confirmed hits + damage
// ---------------------------------------------------------------------------

/**
 * Produce a `!darkfeld-roll` command string for the Phase C (damage-results)
 * dispatch.  The plugin injects this into Roll20's chat textarea after the
 * player has confirmed which attacks landed.
 */
export function formatForRoll20(result: CompletedRollResult): string {
  const is5e = result.gameSystem === 'dnd5e';

  const atks: R20ResultEntry[] = result.resolvedAttacks.map((atk) => {
    const isCrit = atk.isCrit && atk.isHit;
    const base: R20ResultEntry = {
      l:    atk.label,
      d20:  atk.d20,
      mod:  atk.attackMod,
      tot:  atk.total,
      hit:  atk.isHit,
      crit: isCrit,
      vs:   atk.vsTarget,
    };
    // Carry advantage/disadvantage roll data when present
    if (atk.advantageDie !== undefined) {
      base.adv  = atk.advantageDie;
      base.mode = atk.rollMode as 'advantage' | 'disadvantage';
    }
    if (!atk.isHit) return base;

    const dmg: R20DmgRow[] = [];

    // Regular on-hit damage rows
    for (const d of atk.damage) {
      let tot: number;
      if (!isCrit || d.critMult <= 1) {
        tot = d.total;
      } else if (is5e) {
        tot = critDamage(d, is5e, atk.critStyle);
      } else {
        tot = d.total * d.critMult;
      }

      // Build the damage-cell label:
      //   Special type rows → structured label ("Con Damage", "Negative Level", …)
      //   Crit-multiplied HP rows → "Damage ×N" / "Damage (dice ×2)"
      //   Everything else → default "Damage" (no lbl)
      let lbl: string | undefined;
      if (d.specialType) {
        lbl = SPECIAL_DAMAGE_LABELS[d.specialType as SpecialDamageType];
      } else if (isCrit && d.critMult > 1) {
        lbl = is5e
          ? (atk.critStyle === 'perfect' ? 'Damage (max + crit roll)' : 'Damage (dice ×2)')
          : `Damage ×${d.critMult}`;
      }

      dmg.push({
        tot,
        note: d.notes,
        ...(lbl              ? { lbl }      : {}),
        ...(d.specialType    ? { sp: true } : {}),
        // Dice breakdown — lets the Mod script show "2d6 [4, 5] +15" detail
        ...(d.rolls.length > 0 ? { expr: d.diceExpr.replace(/[+-].*/, ''), dice: d.rolls } : {}),
        ...(d.modifier !== 0   ? { dmod: d.modifier } : {}),
      });
    }

    // On-crit-extra rows (never multiplied; shown with a distinct label)
    if (isCrit && atk.damage.length > 0) {
      for (const d of atk.onCritExtraDamage) {
        dmg.push({
          tot:  d.total,
          note: d.notes,
          lbl:  'Bonus (Crit)',
          ...(d.rolls.length > 0 ? { expr: d.diceExpr.replace(/[+-].*/, ''), dice: d.rolls } : {}),
          ...(d.modifier !== 0   ? { dmod: d.modifier } : {}),
        });
      }
    }

    const fx = (atk.specialEffects ?? []).map((e) => ({
      l: e.property === 'vorpal' ? 'Vorpal' : 'Sharpness',
      t: fmtSpecialEffect(e).plain,
    }));

    return { ...base, dmg, ...(fx.length ? { fx } : {}), atkTot: atk.attackDamage };
  });

  // Only include triggered conditional damage
  const cond: R20CondEntry[] = (result.conditionalDamageResults ?? [])
    .filter((c) => c.triggered)
    .map((c) => ({ l: c.label ?? 'Bonus', tot: c.total }));

  // Only include triggered bonus attacks; compute their combined damage total
  const bonus: R20BonusEntry[] = (result.bonusAttackResults ?? [])
    .filter((b) => b.triggered && b.attackResults.length > 0)
    .map((b) => {
      const tot = b.attackResults.reduce((sum, a) => {
        let d = 0;
        for (const row of a.damage) {
          if (!a.isCrit || row.critMult <= 1) {
            d += row.total;
          } else if (is5e) {
            d += critDamage(row, is5e, a.critStyle);
          } else {
            d += row.total * row.critMult;
          }
        }
        return sum + d;
      }, 0);
      return { l: b.label ?? 'Bonus Attack', cnt: b.attackResults.length, tot };
    });

  // Special (non-HP) damage summary for the Mod script card footer.
  // e.g. [{ n: 'Cha Damage', v: 2 }, { n: 'Negative Level', v: 1 }]
  // TODO: update DarkfeldExport Mod script to render spec entries in the card.
  const spec = Object.entries(result.specialDamageTotals ?? {})
    .map(([type, amt]) => ({ n: SPECIAL_DAMAGE_LABELS[type as SpecialDamageType], v: amt! }));

  const payload: Record<string, unknown> = {
    v:         1,
    ph:        'C',
    chr:       result.characterName ?? '',
    rtn:       result.routineName,
    atks,
    ...(cond.length  > 0 ? { cond  } : {}),
    ...(bonus.length > 0 ? { bonus } : {}),
    ...(spec.length  > 0 ? { spec  } : {}),
    totalDmg:  result.totalDamage,
    hits:      result.hits,
    totalAtks: result.totalAttacks,
    mods:      result.appliedModifierNames ?? [],
    theme:     result.r20Theme ?? 'default',
  };

  return `!darkfeld-roll ${JSON.stringify(payload)}`;
}
